import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { HiPlus } from "react-icons/hi";
import AddAvatar from "./AddAvatar";
import { FaEdit, FaLock, FaGlobe } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";
import EditAvatar from "./EditAvatar";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { usePermission } from "Context/PermissionContext";

const AvatarList = ({
  onCountsUpdate,
  filterType = "all",
  setFilterType,
  filterGender = "",
  setFilterGender,
}) => {
  const [addAvatarModal, setAddAvatarModal] = useState(false);
  const [editAvatarModal, setEditAvatarModal] = useState(false);
  const [deleteAvatarModal, setDeleteAvatarModal] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState({});
  const [loading, setLoading] = useState(false);
  const [allAvatars, setAllAvatars] = useState([]);
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.users?.subModule?.Avatar?.permissions;

  const fetchAllAvatars = useCallback(() => {
    setLoading(true);

    const params = {};
    if (filterGender) params.gender = filterGender;
    if (filterType && filterType !== "all") params.type = filterType;

    let config = {
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/avatar/admin/get-all`,
      params,
    };

    axios
      .request(config)
      .then((response) => {
        setAllAvatars(response.data?.avatars || []);
        if (response.data?.counts && onCountsUpdate) {
          onCountsUpdate(response.data.counts);
        }
      })
      .catch((error) => {
        console.log("Error in fetchAllAvatars: ", error);
        setAllAvatars([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filterGender, filterType, onCountsUpdate]);

  useEffect(() => {
    fetchAllAvatars();
  }, [fetchAllAvatars]);

  const handleEditClick = (avatar) => {
    setSelectedAvatar(avatar);
    setEditAvatarModal(true);
  };

  const handleDeleteClick = (avatar) => {
    setSelectedAvatar(avatar);
    setDeleteAvatarModal(true);
  };

  return (
    <React.Fragment>
      <AddAvatar
        visible={addAvatarModal}
        onClose={() => setAddAvatarModal(false)}
        fetchAllAvatars={fetchAllAvatars}
      />

      <EditAvatar
        visible={editAvatarModal}
        avatar={selectedAvatar}
        onClose={() => setEditAvatarModal(false)}
        fetchAllAvatars={fetchAllAvatars}
      />

      <ConfirmDeleteModal
        avatar={selectedAvatar}
        visible={deleteAvatarModal}
        onClose={() => setDeleteAvatarModal(false)}
        fetchAllAvatars={fetchAllAvatars}
      />

      <div className="py-5">
        {/* Controls and Filters */}
        <div className="flex flex-col items-center justify-between gap-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row">
          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Type Filters */}
            <div className="flex rounded-xl bg-gray-100 p-1 text-xs font-semibold">
              <button
                onClick={() => setFilterType && setFilterType("all")}
                className={`rounded-lg px-3 py-1.5 transition-all ${
                  filterType === "all"
                    ? "bg-white text-[#c51369] shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterType && setFilterType("public")}
                className={`rounded-lg px-3 py-1.5 transition-all ${
                  filterType === "public"
                    ? "bg-white text-emerald-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Public
              </button>
              <button
                onClick={() => setFilterType && setFilterType("private")}
                className={`rounded-lg px-3 py-1.5 transition-all ${
                  filterType === "private"
                    ? "bg-white text-amber-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Private
              </button>
            </div>

            {/* Gender Filters */}
            <div className="flex rounded-xl bg-gray-100 p-1 text-xs font-semibold">
              <button
                onClick={() => setFilterGender && setFilterGender("")}
                className={`rounded-lg px-3 py-1.5 transition-all ${
                  filterGender === ""
                    ? "bg-white text-[#c51369] shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                All Genders
              </button>
              <button
                onClick={() => setFilterGender && setFilterGender("male")}
                className={`rounded-lg px-3 py-1.5 transition-all ${
                  filterGender === "male"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Male
              </button>
              <button
                onClick={() => setFilterGender && setFilterGender("female")}
                className={`rounded-lg px-3 py-1.5 transition-all ${
                  filterGender === "female"
                    ? "bg-white text-pink-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Female
              </button>
            </div>
          </div>

          {/* Add Avatar Button */}
          {currentTabPermissions?.create && (
            <button
              onClick={() => setAddAvatarModal(true)}
              className="flex items-center justify-center gap-2 rounded-xl bg-[#c51369] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#a10f5b] hover:shadow-md"
            >
              <HiPlus className="h-5 w-5" />
              <span>Add Avatar</span>
            </button>
          )}
        </div>

        {/* Grid List */}
        <div className="grid grid-cols-1 gap-6 py-5 sm:grid-cols-2 lg:grid-cols-4">
          {loading ? (
            Array(8)
              .fill(null)
              .map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse rounded-lg bg-gray-200 p-4 shadow-md"
                >
                  <div className="mb-4 h-40 rounded-lg bg-gray-300"></div>
                  <div className="mb-2 h-6 rounded bg-gray-300"></div>
                </div>
              ))
          ) : allAvatars?.length === 0 ? (
            <div className="col-span-full rounded-2xl bg-white p-12 text-center text-gray-500 shadow-sm">
              <p className="text-lg font-medium">No avatars found</p>
              <p className="mt-1 text-sm text-gray-400">
                Try selecting different filter options or create a new avatar.
              </p>
            </div>
          ) : (
            allAvatars?.map((avatar, index) => (
              <div
                key={avatar._id || index}
                className="rounded-xl border bg-white shadow-md transition-shadow duration-300 hover:shadow-lg"
              >
                <div className="group relative h-64 overflow-hidden rounded-xl bg-gradient-to-br from-pink-400 to-pink-600 shadow-lg transition duration-300 ease-in-out">
                  {/* Avatar Image */}
                  <img
                    src={avatar.avatarUrl}
                    alt="Avatar"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        avatar.gender || "Avatar"
                      )}&background=c51369&color=fff`;
                    }}
                  />

                  {/* Gender Tag */}
                  <div
                    className={`absolute left-2 top-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white shadow-md backdrop-blur-md ${
                      avatar.gender === "female"
                        ? "bg-pink-500/80"
                        : avatar.gender === "male"
                        ? "bg-blue-500/80"
                        : "bg-purple-500/80"
                    }`}
                  >
                    {avatar.gender || "Other"}
                  </div>

                  {/* Visibility Tag (Public / Private) */}
                  <div
                    className={`absolute left-2 bottom-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white shadow-md backdrop-blur-md ${
                      avatar.isPrivate ? "bg-amber-600/80" : "bg-emerald-600/80"
                    }`}
                  >
                    {avatar.isPrivate ? (
                      <>
                        <FaLock size={10} />
                        <span>Private</span>
                      </>
                    ) : (
                      <>
                        <FaGlobe size={10} />
                        <span>Public</span>
                      </>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    {currentTabPermissions?.edit && (
                      <button
                        onClick={() => handleEditClick(avatar)}
                        title="Edit Avatar"
                        className="flex items-center justify-center rounded-full bg-white/90 p-2 text-gray-700 shadow transition hover:bg-white hover:text-pink-600"
                      >
                        <FaEdit size={14} />
                      </button>
                    )}
                    {currentTabPermissions?.delete && (
                      <button
                        onClick={() => handleDeleteClick(avatar)}
                        title="Delete Avatar"
                        className="flex items-center justify-center rounded-full bg-white/90 p-2 text-gray-700 shadow transition hover:bg-white hover:text-red-600"
                      >
                        <FaTrash size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default AvatarList;
